import { useRef, useState, useEffect, useContext } from "react";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { faUser, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Navigate, useNavigate } from "react-router-dom";
import GlobalContext from "../context/GlobalContext";

const Login = () => {
  const userRef = useRef();
  const errRef = useRef();
  const [name, setName] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { verified, setVerified, finishedLoading, setFinishedLoading, savedEvents } = useContext(GlobalContext);

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.removeItem("token");
    setVerified(false);
    setFinishedLoading("");
    userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [name, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const body = { name, pwd };
      //check credentials
      const res = await fetch(
        "http://opti-plan-2024-backend.vercel.app/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      //store token in local machine
      const parseRes = await res.json();
      if (parseRes.message === 'Invalid Credentials') {
        throw "invalid credentials";
      }
      localStorage.setItem("token", parseRes.token);
      setName("");
      setPwd("");
      setLoggedIn(true);
    } catch (err) {
      setIsLoading(false);
      setErrMsg("Login failed");
      errRef.current.focus();
    }
  };

  async function verify() {
    try {
      const res = await fetch(
        "http://opti-plan-2024-backend.vercel.app/auth/verify",
        {
          method: "GET",
          headers: { token: localStorage.token },
        }
      );
      const parseRes = await res.json();

      setVerified(parseRes.auth);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    verify();
  }, [loggedIn]);

  useEffect(() => {
    if (loggedIn && verified && finishedLoading && savedEvents.message !== 'Token is not valid') {
      setIsLoading(false);
      navigate("/home/home");
    }
  }, [verified, loggedIn, finishedLoading, savedEvents]);

  return (
    <div className="h-screen w-full bg-gradient-to-r from-neutral-800 to-black fixed left-0 top-0 flex items-center">
      <div className="w-5/12 min-w-96 h-full flex items-center shrink-0">
        <div className="w-full">
          <p
            ref={errRef}
            className="text-neutral-200 w-full text-center"
            aria-live="assertive"
          >
            {errMsg}
          </p>
          <h1 className="p-5 pt-8 text-center text-xl text-neutral-200 font-bold">
            Sign In
          </h1>
          <form onSubmit={handleSubmit}>
            <div className="p-3">
              <div className="grid relative items-center text-gray-300 focus-within:text-neutral-200">
                <span className="w-5 h-5 mb-0.5 absolute ml-10 pointer-events-none">
                  <FontAwesomeIcon icon={faUser} />
                </span>
                <input
                  type="text"
                  id="username"
                  placeholder="Username"
                  ref={userRef}
                  autoComplete="off"
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                  required
                  className="pl-12 px-5 py-2 w-11/12 h-12 justify-self-center font-semibold bg-neutral-500 placeholder-gray-300 text-neutral-200 rounded-2xl border-none focus:ring-0"
                />
              </div>
            </div>
            <div className="p-3">
              <div className="grid relative items-center text-gray-300 focus-within:text-neutral-200">
                <span className="w-5 h-5 mb-0.5 absolute ml-10 pointer-events-none">
                  <FontAwesomeIcon icon={faLock} />
                </span>
                <input
                  type={pwdVisible ? "text" : "password"}
                  id="password"
                  placeholder="Password"
                  onChange={(e) => setPwd(e.target.value)}
                  value={pwd}
                  required
                  className="pl-12 px-5 py-2 w-11/12 h-12 justify-self-center font-semibold bg-neutral-500 placeholder-gray-300 text-neutral-200 rounded-2xl border-none focus:ring-0"
                />
                <div
                  className="w-5 h-5 mb-0.5 absolute justify-self-end mr-10 text-gray-300 focus-within:text-neutral-200"
                  onClick={() => setPwdVisible(!pwdVisible)}
                >
                  {pwdVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </div>
              </div>
            </div>
            <div className="grid p-3 relative items-center">
              {isLoading &&
                <div
                  className="inline-block absolute justify-self-end mr-[51px] text-neutral-200 h-7 w-7 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                >
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                    Loading...
                  </span>
                </div>
              }
              <button
                type="submit"
                disabled={!name || !pwd ? true : false}
                className={
                  !name || !pwd
                    ? "w-11/12 h-12 justify-self-center rounded-2xl border-none bg-neutral-500 text-neutral-200"
                    : "w-11/12 h-12 justify-self-center rounded-2xl border-none ring-2 bg-gradient-to-r from-sky-500 to-indigo-500 text-neutral-200"
                }
              >
                Sign In
              </button>
            </div>
          </form>
          <div className="p-3 flex items-center justify-center">
            <p className="text-neutral-200">Haven't registered?</p>
          </div>
          <div className="grid p-3 pb-6">
            <span className="w-11/12 h-12 justify-self-center rounded-2xl border-none ring-2 bg-gradient-to-r from-sky-500 to-indigo-500 text-neutral-200">
              <a
                className="w-full h-full flex items-center justify-center"
                href="/register"
              >
                Sign up
              </a>
            </span>
          </div>
        </div>
      </div>
      <div className="w-7/12 h-screen">
        <img
          src="/images/tagline.png"
          className="absolute top-0 h-2/5 pt-14 pl-14"
        />
        <img
          src="/images/optiplanNoBg.gif"
          className="absolute bottom-0 right-0 m-12 h-[30vh]"
        />
      </div>
    </div>
  );
};

export default Login;
